import { useContext } from 'react';
import { ReactRelayContext } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { getBylineAppContentPropertyKeys } from '@confluence/forge-ui/entry-points/bylineContentPropertyKeys.ts';

import type { ByLineForgeAppGeneralContentPropertyQuery$data } from './__generated__/ByLineForgeAppGeneralContentPropertyQuery.graphql.ts';
import ByLineForgeAppGeneralContentPropertyQuery from './__generated__/ByLineForgeAppGeneralContentPropertyQuery.graphql.ts';
import type { ContentPropertyConnection } from './validateContentPropertyData.tsx';

export const useBylineAppContentProperties = (
	contentId: string,
	enabled = true,
): ContentPropertyConnection | undefined => {
	const relayContext = useContext(ReactRelayContext);
	if (
		!enabled ||
		!contentId ||
		!relayContext ||
		// Cache lookup alone is not exposure; SingleLineByLine exposes eligible SSR/hydration loads.
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
		!UNSAFE_expValEqualsNoExposure('confluence_ssr_byline', 'isEnabled', true)
	) {
		return undefined;
	}

	const keys = getBylineAppContentPropertyKeys();
	if (keys.length === 0) {
		return undefined;
	}

	// Read only the completed preload for the initial SSR/hydration snapshot. Do not fetch,
	// suspend, or subscribe here: runtime apps own fetching and subsequent property updates.
	const operation = createOperationDescriptor(
		getRequest(ByLineForgeAppGeneralContentPropertyQuery),
		{
			contentId,
			keys,
		},
	);
	const snapshot = relayContext.environment.lookup(operation.fragment);
	const data = snapshot.data as ByLineForgeAppGeneralContentPropertyQuery$data | undefined;
	return snapshot.isMissingData ? undefined : (data?.content?.nodes?.[0]?.properties ?? undefined);
};
